import { Box, Skeleton, Flex } from '@chakra-ui/react';
import { Button, Heading, Card, CardBody } from 'components/v4';
import { useNavigate, useParams } from 'react-router-dom';

import { axios } from '@/services/axios';
import { useQuery } from 'hooks/useCustomQuery';
import { differenceBy } from 'lodash';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ServiceStatus from './ServiceStatus';
import TransferringTables from 'components/tables/TransferringTables/TransferringTables';
import config from '@/config.json';
import StatusCellRenderer from 'components/v4/StatusCellRenderer';

export default function ActivateServiceUsers() {
    const { id: serviceId } = useParams();
    const navigate = useNavigate();

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { darkMode } = useSelector((state) => state.settings);

    // left side table (users pool)
    const [usersAvailable, setUsersAvailable] = useState([]);
    // right side table (activated/yet to be activated users)
    const [usersChosen, setUsersChosen] = useState([]);
    // service users that are already activated
    const [activatedUsers, setActivatedUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // service status
    const [success, setSuccess] = useState(0);
    const [failed, setFailed] = useState(0);
    const [activateError, setActivateError] = useState({
        count: 0,
        message: '',
    });
    const [deactivateError, setDeactivateError] = useState({
        count: 0,
        message: '',
    });
    const [total, setTotal] = useState(0);

    // get users available to be activated for the service
    const { data: service, isFetching: serviceLoading } = useQuery({
        queryKey: ['service', serviceId],
        queryFn: async () => {
            const res = await axios.get(`service/${serviceId}`);
            return res.data;
        },
        enabled: Boolean(serviceId),
        refetchOnWindowFocus: false,
    });

    // get users available to be activated for the service
    const { data: companyUsers, isFetching: companyUsersLoading } = useQuery({
        queryKey: ['companyUsers'],
        queryFn: async () => {
            const res = await axios.get(`users/${currentCompany}`);
            return res.data;
        },
        enabled: Boolean(currentCompany),
        onSuccess: async (data) => {
            // users avaialble to be activated
            const transformedData = data.map((user) => ({
                ...user,
                statusText:
                    config.users.status[String(user.status)] || user.status,
                statusColor:
                    config.users.statusColor[String(user.status)] || 'gray',
            }));
            setUsersAvailable(transformedData);
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
        },
        refetchOnWindowFocus: false,
    });

    // get activated users for the service
    const { isFetching: serviceUsersLoading } = useQuery({
        queryKey: ['serviceUsers', serviceId],
        queryFn: async () => {
            const res = await axios.get(
                `service/vendordefined/${serviceId}/users`,
            );
            return res.data;
        },
        // service id, company users is not loading and company users is greater than 0, then fetch service users
        enabled:
            Boolean(serviceId) &&
            !companyUsersLoading &&
            companyUsers?.length > 0,
        onSuccess: async (data) => {
            if (usersAvailable) {
                let ua = [...usersAvailable];
                let usersChosen = [];

                ua = ua.filter((user) => {
                    if (data.includes(user.id)) {
                        usersChosen.push(user);
                        return false; // Exclude this user from usersAvailable
                    }
                    return true; // Keep this user in usersAvailable
                });

                // Push filtered available users
                setUsersAvailable(ua);
                // Push chosen users into usersChosen
                setUsersChosen(usersChosen);
                // users that are already activated
                setActivatedUsers(usersChosen);
            }
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
        },
        refetchOnWindowFocus: false,
    });

    const handleBackButton = () => {
        setIsLoading(false);
        setActivateError({
            count: 0,
            message: '',
        });
        setDeactivateError({
            count: 0,
            message: '',
        });
        setTotal(0);
        setSuccess(0);
        setFailed(0);
        navigate('/dashboard-services');
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        let failedDeactivateUsers = [];
        let failedActivateUsers = [];
        try {
            let activateUsers = differenceBy(usersChosen, activatedUsers, 'id');
            let deactivateUsers = differenceBy(
                activatedUsers,
                usersChosen,
                'id',
            );

            setTotal(activateUsers.length + deactivateUsers.length);

            // activate service users
            for (const user of activateUsers) {
                try {
                    await axios.post(
                        `/service/vendordefined/${serviceId}/user/${user.id}/activate`,
                    );
                    setSuccess((prev) => prev + 1);
                } catch (e) {
                    setFailed((prev) => prev + 1);
                    failedActivateUsers.push(user);
                }
            }

            // deactivate service users
            for (const user of deactivateUsers) {
                try {
                    await axios.post(
                        `/service/vendordefined/${serviceId}/user/${user.id}/deactivate`,
                    );
                    setSuccess((prev) => prev + 1);
                } catch (e) {
                    setFailed((prev) => prev + 1);
                    failedDeactivateUsers.push(user);
                }
            }
        } catch (e) {
            toast.error(e?.response?.data?.message);
        } finally {
            // setIsLoading(false);
            if (failedActivateUsers.length > 0) {
                let message = '';
                for (const [index, user] of failedActivateUsers.entries()) {
                    if (index !== failedActivateUsers.length - 1) {
                        message = message.concat(user.userPrincipalName + ', ');
                    } else {
                        message = message.concat(user.userPrincipalName + '.');
                    }
                }
                setActivateError((prevState) => ({
                    ...prevState,
                    count: failedActivateUsers.length,
                    message: message,
                }));
            }

            if (failedDeactivateUsers.length > 0) {
                let message = '';
                for (const [index, user] of failedDeactivateUsers.entries()) {
                    if (index !== failedDeactivateUsers.length - 1) {
                        message = message.concat(user.userPrincipalName + ', ');
                    } else {
                        message = message.concat(user.userPrincipalName + '.');
                    }
                }
                setDeactivateError((prevState) => ({
                    ...prevState,
                    count: failedDeactivateUsers.length,
                    message: message,
                }));
            }
        }
    };

    const userColDefs = [
        {
            label: 'User Principal Name',
            dataKey: 'o365UserPrincipalName',
            width: 280,
            minWidth: 280,
            flexGrow: 0,
            maxWidth: 280,
        },
        {
            label: 'Status',
            dataKey: 'statusText',
            width: 120,
            minWidth: 120,
            flexGrow: 0,
            maxWidth: 120,
            cellRenderer: ({ rowData }) => {
                const statuses = {
                    green: {
                        type: 'active',
                        displayedName: rowData.statusText,
                    },
                    red: {
                        type: 'inactive',
                        displayedName: rowData.statusText,
                    },
                    '#c1c92a': {
                        type: 'processing',
                        displayedName: rowData.statusText,
                    },
                    yellow: {
                        type: 'processing',
                        displayedName: rowData.statusText,
                    },
                };
                return (
                    <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <StatusCellRenderer
                            value={rowData.statusColor}
                            statuses={statuses}
                            isSmall={true}
                        />
                    </div>
                );
            },
        },
        {
            isCheckbox: true,
            label: 'Action',
            dataKey: 'checkbox',
            width: 80,
            minWidth: 80,
            maxWidth: 80,
        },
    ];

    const availableConfig = {
        captionName: 'Available Users ',
        placeholder: 'Search users...',
        colDefs: userColDefs,
        searchField: 'o365UserPrincipalName',
    };

    const chosenConfig = {
        captionName: 'Chosen Users ',
        placeholder: 'Search users...',
        colDefs: userColDefs,
        searchField: 'o365UserPrincipalName',
    };

    const walkthroughIds = {
        moveToChosen: '/service-profiles/button/move/chosen',
        moveToAvailable: '/service-profiles/button/move/available',
        moveAllToChosen: '/service-profiles/button/move/chosen/all',
        moveAllToAvailable: '/service-profiles/button/move/available/all',
    };

    return (
        <Box maxW="1200px">
            <Card
                bg={darkMode ? 'dark.tcap' : null}
                borderRadius="8px"
                boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
                mb={6}>
                <CardBody p={6}>
                    <Heading fontSize="2xl" fontWeight="bold" mb={4}>
                        Activate Service Users
                        {serviceLoading ||
                        companyUsersLoading ||
                        serviceUsersLoading ? (
                            <Skeleton
                                mt={1}
                                w={200}
                                h={8}
                                ml={2}
                                display="inline-block"
                            />
                        ) : (
                            <> : {service?.name || 'Vendor Defined'}</>
                        )}
                    </Heading>

                    {isLoading ? (
                        <ServiceStatus
                            title="Users"
                            failedCount={failed}
                            activateError={activateError}
                            deactivateError={deactivateError}
                            successCount={success}
                            totalCount={total}
                            handleBack={handleBackButton}
                        />
                    ) : (
                        <>
                            <TransferringTables
                                availableData={usersAvailable}
                                chosenData={usersChosen}
                                availableTableConfig={availableConfig}
                                chosenTableConfig={chosenConfig}
                                isLoading={
                                    companyUsersLoading || serviceUsersLoading
                                }
                                isDisabled={isLoading}
                                onAvailableDataChange={setUsersAvailable}
                                onChosenDataChange={setUsersChosen}
                                walkthroughIds={walkthroughIds}
                            />

                            <Flex gap={4} mt={8} justifyContent="center">
                                <Button
                                    variant="outline"
                                    onClick={handleBackButton}
                                    isDisabled={isLoading}
                                    data-walkthroughid="/service-profiles/button/back">
                                    Back
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    isLoading={isLoading}
                                    data-walkthroughid="/service-profiles/button/save">
                                    Save
                                </Button>
                            </Flex>
                        </>
                    )}
                </CardBody>
            </Card>
        </Box>
    );
}
